// src/pages/InventoryCheck.js
// 前台點貨頁面：依位置分類，+/- 快速輸入，即時同步

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import socket from '../lib/socket';

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const WD_LABEL = ['日', '一', '二', '三', '四', '五', '六'];

function round1(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.round((x + Number.EPSILON) * 10) / 10;
}

function formatQty(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '0';
  return String(round1(x)).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

/** @param {string} ymd YYYY-MM-DD */
function weekdayIndexFromYmd(ymd) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  if (!y || !m || !d) return new Date().getDay();
  return new Date(y, m - 1, d).getDay();
}

export default function InventoryCheck({ embedFromShift = false }) {
  const [date, setDate] = useState(todayYmd);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [records, setRecords] = useState({});
  const [checkers, setCheckers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [onlyUnchecked, setOnlyUnchecked] = useState(false);
  const [checkerName, setCheckerName] = useState(() => {
    if (embedFromShift) {
      try {
        const { getShiftToken } = require('../lib/shiftApi');
        const t = getShiftToken();
        if (t) {
          const b64 = t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
          const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
          const binary = atob(padded);
          const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
          const payload = JSON.parse(new TextDecoder('utf-8').decode(bytes));
          return payload.name || '';
        }
      } catch { /* ignore */ }
    }
    try { return localStorage.getItem('inv-checker-name') || ''; } catch { return ''; }
  });
  const checkerRef = useRef(checkerName);
  const debounceRef = useRef({});
  const abortRef = useRef({});

  const shiftAuthHeader = () => {
    if (!embedFromShift) return {};
    try {
      const { getShiftToken } = require('../lib/shiftApi');
      const t = getShiftToken();
      return t ? { Authorization: `Bearer ${t}` } : {};
    } catch { return {}; }
  };

  useEffect(() => {
    document.title = '點貨系統';
    return () => { document.title = '齊文藝室會員系統'; };
  }, []);

  const loadBase = useCallback(async () => {
    try {
      const [locRes, itemRes] = await Promise.all([
        fetch('/api/inventory/locations').then(r => r.json()),
        fetch('/api/inventory/items').then(r => r.json()),
      ]);
      if (locRes.success) setLocations(locRes.list);
      if (itemRes.success) setItems(itemRes.list.filter(i => i.is_active));
    } catch (e) { console.error(e); }
  }, []);

  const loadRecords = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/inventory/records?date=${d}`);
      const data = await res.json();
      if (data.success) {
        const map = {};
        const cmap = {};
        data.records.forEach(r => {
          map[r.item_id] = r.quantity == null ? '' : formatQty(r.quantity);
          cmap[r.item_id] = {
            name: r.checker_name || '',
            code: r.checker_code || '',
          };
        });
        setRecords(map);
        setCheckers(cmap);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { loadRecords(date); }, [date, loadRecords]);

  useEffect(() => {
    const onRecord = (data) => {
      if (data.check_date === date) {
        setRecords(prev => ({ ...prev, [data.item_id]: data.quantity == null ? '' : formatQty(data.quantity) }));
        setCheckers(prev => ({
          ...prev,
          [data.item_id]: {
            name: data.checker_name || prev[data.item_id]?.name || '',
            code: data.checker_code || prev[data.item_id]?.code || '',
          },
        }));
      }
    };
    const onItems = () => { loadBase(); };
    socket.on('inventory:record', onRecord);
    socket.on('inventory:items', onItems);
    socket.on('inventory:locations', loadBase);
    return () => {
      socket.off('inventory:record', onRecord);
      socket.off('inventory:items', onItems);
      socket.off('inventory:locations', loadBase);
    };
  }, [date, loadBase]);

  const handleCheckerChange = useCallback((val) => {
    setCheckerName(val);
    checkerRef.current = val;
    try { localStorage.setItem('inv-checker-name', val); } catch {}
  }, []);

  const saveRecord = useCallback((itemId, qty) => {
    if (abortRef.current[itemId]) abortRef.current[itemId].abort();
    const ac = new AbortController();
    abortRef.current[itemId] = ac;
    setSaving(prev => ({ ...prev, [itemId]: true }));
    // 樂觀更新點貨人
    setCheckers(prev => ({
      ...prev,
      [itemId]: { name: checkerRef.current || '', code: prev[itemId]?.code || '' },
    }));
    fetch('/api/inventory/records/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...shiftAuthHeader() },
      body: JSON.stringify({ item_id: itemId, check_date: date, quantity: qty, checker_name: checkerRef.current }),
      signal: ac.signal,
    })
      .then(r => r.json())
      .catch((e) => {
        if (e?.name !== 'AbortError') console.error(e);
      })
      .finally(() => {
        if (abortRef.current[itemId] === ac) {
          setSaving(prev => ({ ...prev, [itemId]: false }));
        }
      });
  }, [date, embedFromShift]);

  const debouncedSave = useCallback((itemId, qty) => {
    clearTimeout(debounceRef.current[itemId]);
    debounceRef.current[itemId] = setTimeout(() => saveRecord(itemId, qty), 400);
  }, [saveRecord]);

  const handleQtyChange = useCallback((itemId, val) => {
    setRecords(prev => ({ ...prev, [itemId]: val }));
    const qty = val === '' ? 0 : (parseFloat(val) || 0);
    debouncedSave(itemId, qty);
  }, [debouncedSave]);

  const handleIncrement = useCallback((itemId, delta) => {
    setRecords(prev => {
      const cur = parseFloat(prev[itemId]) || 0;
      const next = round1(Math.max(0, cur + delta));
      debouncedSave(itemId, next);
      return { ...prev, [itemId]: formatQty(next) };
    });
  }, [debouncedSave]);

  const selectedWeekday = useMemo(() => weekdayIndexFromYmd(date), [date]);

  /** 依「所選日期」的週幾，只顯示排程含該週的商品 */
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const wds = item.weekdays || [];
      if (!wds.includes(selectedWeekday)) return false;
      if (onlyUnchecked) {
        const q = records[item.id];
        return q == null || q === '';
      }
      return true;
    });
  }, [items, selectedWeekday, onlyUnchecked, records]);

  const grouped = useMemo(() => {
    const locMap = {};
    locations.forEach(l => { locMap[l.id] = l; });
    const groups = {};
    filteredItems.forEach(item => {
      const locId = item.location_id || 0;
      const locName = locId ? (locMap[locId]?.name || '未知位置') : '未分配';
      const sortKey = locId ? (locMap[locId]?.sort_order ?? 999) : 9999;
      if (!groups[locId]) groups[locId] = { name: locName, sortKey, items: [] };
      groups[locId].items.push(item);
    });
    return Object.values(groups).sort((a, b) => a.sortKey - b.sortKey);
  }, [filteredItems, locations]);

  // 首次載入時預設全部收合
  const initializedRef = useRef(false);
  useEffect(() => {
    if (grouped.length > 0 && !initializedRef.current) {
      initializedRef.current = true;
      const c = {};
      grouped.forEach(g => { c[g.name] = true; });
      setCollapsed(c);
    }
  }, [grouped]);

  return (
    <div style={{
      minHeight: embedFromShift ? 'auto' : '100vh',
      background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
      fontFamily: 'system-ui, "Microsoft JhengHei", sans-serif',
    }}>
      {/* Header */}
      <div style={{
        position: embedFromShift ? 'relative' : 'sticky', top: 0, zIndex: 100,
        background: '#fff', borderBottom: '1px solid #e2e8f0',
        padding: '14px 16px 10px', boxShadow: '0 1px 4px rgba(0,0,0,.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1e293b', whiteSpace: 'nowrap' }}>點貨系統</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="text"
              value={checkerName}
              onChange={e => !embedFromShift && handleCheckerChange(e.target.value)}
              readOnly={embedFromShift}
              placeholder="點貨人"
              title={embedFromShift ? '班別登入員工（不可改）' : undefined}
              style={{
                padding: '7px 10px', borderRadius: 8, border: '1px solid #cbd5e1',
                fontSize: 15, fontWeight: 600, color: '#334155', width: 80,
                background: embedFromShift ? '#f1f5f9' : '#fff',
              }}
            />
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              style={{
                padding: '7px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
                fontSize: 16, fontWeight: 600, color: '#334155',
              }}
            />
          </div>
        </div>
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#64748b' }}>
            所選日期為「週{WD_LABEL[selectedWeekday]}」— 以下為排程於本日點貨的商品
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 700, color: '#334155', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={onlyUnchecked} onChange={e => setOnlyUnchecked(e.target.checked)} />
            僅未點
          </label>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '10px 8px 80px' }}>
        {loading ? (
          <p style={{ textAlign: 'center', padding: 32, color: '#64748b', fontSize: 17 }}>載入中…</p>
        ) : grouped.length === 0 ? (
          <p style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 17 }}>尚無商品，請先至後台建立。</p>
        ) : (
          grouped.map(group => {
            const total = group.items.length;
            const checked = group.items.filter(item => records[item.id] != null && records[item.id] !== '').length;
            const allDone = checked === total;
            const isCollapsed = collapsed[group.name] || false;
            return (
              <div key={group.name} style={{ marginBottom: 14 }}>
                <div
                  onClick={() => setCollapsed(prev => ({ ...prev, [group.name]: !isCollapsed }))}
                  style={{
                    padding: '10px 14px', background: '#334155', color: '#fff',
                    borderRadius: isCollapsed ? 10 : '10px 10px 0 0',
                    fontSize: 17, fontWeight: 700, cursor: 'pointer', userSelect: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    transition: 'border-radius .15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      display: 'inline-block', transition: 'transform .2s',
                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                      fontSize: 14,
                    }}>▼</span>
                    {group.name}
                  </div>
                  <span style={{
                    fontSize: 14, fontWeight: 700,
                    background: allDone ? 'rgba(74,222,128,.3)' : 'rgba(255,255,255,.2)',
                    padding: '2px 10px', borderRadius: 999,
                  }}>
                    {checked}/{total}
                  </span>
                </div>
                {!isCollapsed && (
                  <div style={{
                    background: '#fff', borderRadius: '0 0 10px 10px',
                    border: '1px solid #e2e8f0', borderTop: 'none',
                  }}>
                    {group.items.map((item, idx) => {
                      const qty = records[item.id] ?? '';
                      const isUnchecked = qty === '' || qty == null;
                      const qtyNum = parseFloat(qty) || 0;
                      const safety = parseFloat(item.safety_line) || 0;
                      const hasQty = qty !== '' && qty != null;
                      const belowSafety = hasQty && safety > 0 && qtyNum <= safety;
                      const isSaving = saving[item.id];
                      const ck = checkers[item.id];
                      const checkerLabel = ck?.name
                        ? (ck.code ? `${ck.name}（${ck.code}）` : ck.name)
                        : '';
                      return (
                        <div
                          key={item.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '12px 14px',
                            borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none',
                            background: isUnchecked
                              ? '#fffbeb'
                              : (belowSafety ? '#fef2f2' : (idx % 2 === 0 ? '#fff' : '#f8fafc')),
                            transition: 'background .2s',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 17, fontWeight: 600, color: '#1e293b',
                              wordBreak: 'break-word', whiteSpace: 'normal',
                              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                            }}>
                              {item.name}
                              {isUnchecked && (
                                <span style={{
                                  fontSize: 12, fontWeight: 800, color: '#b45309',
                                  background: '#fef3c7', border: '1px solid #fde68a',
                                  borderRadius: 6, padding: '1px 6px',
                                }}>未點</span>
                              )}
                              {item.standing_stock != null && (
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#0f766e' }}>常備 {formatQty(item.standing_stock)}</span>
                              )}
                              {belowSafety && <span style={{ color: '#ef4444', fontSize: 13 }}>≤{formatQty(safety)}</span>}
                            </div>
                            {checkerLabel ? (
                              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>點貨：{checkerLabel}</div>
                            ) : null}
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
                            <button
                              type="button"
                              onClick={() => handleIncrement(item.id, -1)}
                              style={{
                                width: 40, height: 40, borderRadius: '8px 0 0 8px',
                                border: '1px solid #cbd5e1', borderRight: 'none',
                                background: '#f8fafc', fontSize: 22, fontWeight: 700,
                                color: '#475569', cursor: 'pointer', lineHeight: 1,
                              }}
                            >−</button>
                            <input
                              type="number"
                              inputMode="decimal"
                              step="0.1"
                              value={qty}
                              onChange={e => handleQtyChange(item.id, e.target.value)}
                              placeholder="0"
                              style={{
                                width: 60, height: 40, textAlign: 'center',
                                border: '1px solid #cbd5e1', borderRadius: 0,
                                fontSize: 18, fontWeight: 700, color: '#1e293b',
                                outline: 'none',
                                background: isSaving ? '#e0f2fe' : '#fff',
                                transition: 'background .2s',
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleIncrement(item.id, 1)}
                              style={{
                                width: 40, height: 40, borderRadius: '0 8px 8px 0',
                                border: '1px solid #cbd5e1', borderLeft: 'none',
                                background: '#f8fafc', fontSize: 22, fontWeight: 700,
                                color: '#475569', cursor: 'pointer', lineHeight: 1,
                              }}
                            >+</button>
                            <span style={{
                              fontSize: 15, color: '#64748b', fontWeight: 600,
                              marginLeft: 8, minWidth: 28, whiteSpace: 'nowrap',
                            }}>
                              {item.unit}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
